import { useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import knowledgeApi from '@src/api/knowledgeBase';
import { useMessage } from '@src/context/common';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const TABS = [
  { value: 'client', label: 'Client knowledge' },
  { value: 'company', label: 'Company knowledge' },
  { value: 'memory', label: 'Client memory' },
  { value: 'company-memory', label: 'Company memory' },
];

const KNOWLEDGE_DOCUMENT_TYPES = [
  { value: 'policy', label: 'Policy' },
  { value: 'faq', label: 'FAQ' },
  { value: 'playbook', label: 'Playbook' },
  { value: 'meeting_note', label: 'Meeting note' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'billing', label: 'Billing' },
  { value: 'preference', label: 'Preference' },
  { value: 'other', label: 'Other' },
];

const KNOWLEDGE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const MEMORY_CATEGORIES = [
  { value: 'preference', label: 'Preference' },
  { value: 'decision', label: 'Decision' },
  { value: 'constraint', label: 'Constraint' },
  { value: 'stakeholder', label: 'Stakeholder' },
  { value: 'open_issue', label: 'Open issue' },
  { value: 'tone', label: 'Tone' },
  { value: 'process', label: 'Process' },
  { value: 'performance', label: 'Performance' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
];

const MEMORY_SOURCE_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'system', label: 'System' },
];

const defaultKnowledgeFormValues = {
  title: '',
  content: '',
  tags: '',
  recency: dayjs(),
  client: null,
  audience: '',
  document_type: 'other',
  status: 'active',
  source_of_truth: '',
  priority: 3,
  useStructuredDraft: true,
  purpose: '',
  policy: '',
  how_it_works: '',
  positioning: '',
  response_template: '',
  faq: '',
  restrictions: '',
  escalation: '',
  notes: '',
};

const defaultMemoryFormValues = {
  client: null,
  audience: '',
  category: 'other',
  source_type: 'manual',
  title: '',
  summary: '',
  details: '',
  tags: '',
  confidence: 1,
  priority: 3,
  is_active: true,
  valid_from: dayjs(),
  valid_until: null,
  last_observed_at: dayjs(),
};

const toTagString = (tags) => (Array.isArray(tags) && tags.length ? tags.join(', ') : '');

const parseTags = (value) =>
  String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const getResults = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  return [];
};

const renderPreview = (value, lines = 2) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: lines,
      WebkitBoxOrient: 'vertical',
    }}
  >
    {value || '—'}
  </Typography>
);

function buildStructuredKnowledgeContent(formValues) {
  const sections = [
    ['Purpose', formValues.purpose],
    ['Policy / Truth', formValues.policy],
    ['How It Works', formValues.how_it_works],
    ['Positioning Notes', formValues.positioning],
    ['Client-Facing Template', formValues.response_template],
    ['FAQ', formValues.faq],
    ['Restrictions / Do Not Say', formValues.restrictions],
    ['Escalation', formValues.escalation],
    ['Additional Notes', formValues.notes],
  ];

  return sections
    .filter(([, value]) => String(value || '').trim())
    .map(([heading, value]) => `${heading}\n${String(value || '').trim()}`)
    .join('\n\n');
}

function KnowledgeBasePage() {
  const message = useMessage();
  const [activeTab, setActiveTab] = useState('client');

  const [clientDocs, setClientDocs] = useState([]);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const [companyDocs, setCompanyDocs] = useState([]);
  const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
  const [companyAudience, setCompanyAudience] = useState('');
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const [memoryItems, setMemoryItems] = useState([]);
  const [memoryItemsLoading, setMemoryItemsLoading] = useState(false);
  const [memoryClientFilter, setMemoryClientFilter] = useState(null);
  const [memorySearchInput, setMemorySearchInput] = useState('');
  const [memorySearch, setMemorySearch] = useState('');
  const [memoryCategoryFilter, setMemoryCategoryFilter] = useState('all');
  const [memoryActiveFilter, setMemoryActiveFilter] = useState('active');
  const [companyMemoryItems, setCompanyMemoryItems] = useState([]);
  const [companyMemoryItemsLoading, setCompanyMemoryItemsLoading] = useState(false);
  const [companyMemoryAudienceFilter, setCompanyMemoryAudienceFilter] = useState('');
  const [companyMemorySearchInput, setCompanyMemorySearchInput] = useState('');
  const [companyMemorySearch, setCompanyMemorySearch] = useState('');
  const [companyMemoryCategoryFilter, setCompanyMemoryCategoryFilter] = useState('all');
  const [companyMemoryActiveFilter, setCompanyMemoryActiveFilter] = useState('active');

  const [dialogType, setDialogType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [knowledgeFormValues, setKnowledgeFormValues] = useState(defaultKnowledgeFormValues);
  const [memoryFormValues, setMemoryFormValues] = useState(defaultMemoryFormValues);
  const [submitting, setSubmitting] = useState(false);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientAutocompleteInput,
    addOption: addClientOption,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  useEffect(() => {
    const handle = setTimeout(() => setClientSearch(clientSearchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [clientSearchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setCompanySearch(companySearchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [companySearchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setMemorySearch(memorySearchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [memorySearchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setCompanyMemorySearch(companyMemorySearchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [companyMemorySearchInput]);

  const loadClientDocs = useCallback(() => {
    setClientDocsLoading(true);
    knowledgeApi
      .listClientDocuments({ client: clientFilter, search: clientSearch })
      .then((response) => {
        const docs = getResults(response?.data);
        setClientDocs(docs);
        docs.forEach((doc) => {
          if (doc?.client && doc?.client_name) {
            addClientOption({ value: doc.client, label: doc.client_name });
          }
        });
      })
      .catch((error) => {
        console.error('Failed to load client knowledge documents', error);
        message.error('Unable to load client knowledge documents.');
      })
      .finally(() => setClientDocsLoading(false));
  }, [addClientOption, clientFilter, clientSearch, message]);

  const loadCompanyDocs = useCallback(() => {
    setCompanyDocsLoading(true);
    knowledgeApi
      .listCompanyDocuments({ audience: companyAudience, search: companySearch })
      .then((response) => setCompanyDocs(getResults(response?.data)))
      .catch((error) => {
        console.error('Failed to load company knowledge documents', error);
        message.error('Unable to load company knowledge documents.');
      })
      .finally(() => setCompanyDocsLoading(false));
  }, [companyAudience, companySearch, message]);

  const loadMemoryItems = useCallback(() => {
    setMemoryItemsLoading(true);
    const params = {
      client: memoryClientFilter,
      search: memorySearch,
    };
    if (memoryCategoryFilter !== 'all') {
      params.category = memoryCategoryFilter;
    }
    if (memoryActiveFilter === 'active') {
      params.is_active = true;
    } else if (memoryActiveFilter === 'inactive') {
      params.is_active = false;
    }
    knowledgeApi
      .listClientMemoryItems(params)
      .then((response) => {
        const items = getResults(response?.data);
        setMemoryItems(items);
        items.forEach((item) => {
          if (item?.client && item?.client_name) {
            addClientOption({ value: item.client, label: item.client_name });
          }
        });
      })
      .catch((error) => {
        console.error('Failed to load client memory items', error);
        message.error('Unable to load client memory items.');
      })
      .finally(() => setMemoryItemsLoading(false));
  }, [
    addClientOption,
    memoryActiveFilter,
    memoryCategoryFilter,
    memoryClientFilter,
    memorySearch,
    message,
  ]);

  const loadCompanyMemoryItems = useCallback(() => {
    setCompanyMemoryItemsLoading(true);
    const params = {
      audience: companyMemoryAudienceFilter,
      search: companyMemorySearch,
    };
    if (companyMemoryCategoryFilter !== 'all') {
      params.category = companyMemoryCategoryFilter;
    }
    if (companyMemoryActiveFilter === 'active') {
      params.is_active = true;
    } else if (companyMemoryActiveFilter === 'inactive') {
      params.is_active = false;
    }
    knowledgeApi
      .listCompanyMemoryItems(params)
      .then((response) => {
        setCompanyMemoryItems(getResults(response?.data));
      })
      .catch((error) => {
        console.error('Failed to load company memory items', error);
        message.error('Unable to load company memory items.');
      })
      .finally(() => setCompanyMemoryItemsLoading(false));
  }, [
    companyMemoryActiveFilter,
    companyMemoryAudienceFilter,
    companyMemoryCategoryFilter,
    companyMemorySearch,
    message,
  ]);

  useEffect(() => {
    loadClientDocs();
  }, [loadClientDocs]);

  useEffect(() => {
    loadCompanyDocs();
  }, [loadCompanyDocs]);

  useEffect(() => {
    loadMemoryItems();
  }, [loadMemoryItems]);

  useEffect(() => {
    loadCompanyMemoryItems();
  }, [loadCompanyMemoryItems]);

  const openKnowledgeDialog = useCallback(
    (type, item = null) => {
      setDialogType(type);
      setEditingItem(item);
      if (item?.client && item?.client_name) {
        addClientOption({ value: item.client, label: item.client_name });
      }
      setKnowledgeFormValues({
        ...defaultKnowledgeFormValues,
        title: item?.title || '',
        content: item?.content || '',
        tags: toTagString(item?.tags),
        recency: item?.recency ? dayjs(item.recency) : dayjs(),
        client: type === 'client' ? item?.client ?? null : null,
        audience: type === 'company' ? item?.audience || '' : '',
        document_type: item?.document_type || 'other',
        status: item?.status || 'active',
        source_of_truth: item?.source_of_truth || '',
        priority: item?.priority ?? 3,
        useStructuredDraft: !item,
      });
    },
    [addClientOption],
  );

  const openMemoryDialog = useCallback(
    (type = 'memory', item = null) => {
      setDialogType(type);
      setEditingItem(item);
      if (item?.client && item?.client_name) {
        addClientOption({ value: item.client, label: item.client_name });
      }
      setMemoryFormValues({
        ...defaultMemoryFormValues,
        client: type === 'memory' ? item?.client ?? null : null,
        audience: type === 'company-memory' ? item?.audience || '' : '',
        category: item?.category || 'other',
        source_type: item?.source_type || 'manual',
        title: item?.title || '',
        summary: item?.summary || '',
        details: item?.details || '',
        tags: toTagString(item?.tags),
        confidence: Number(item?.confidence ?? 1),
        priority: item?.priority ?? 3,
        is_active: item?.is_active ?? true,
        valid_from: item?.valid_from ? dayjs(item.valid_from) : dayjs(),
        valid_until: item?.valid_until ? dayjs(item.valid_until) : null,
        last_observed_at: item?.last_observed_at ? dayjs(item.last_observed_at) : dayjs(),
      });
    },
    [addClientOption],
  );

  const closeDialog = useCallback(() => {
    setDialogType(null);
    setEditingItem(null);
    setKnowledgeFormValues(defaultKnowledgeFormValues);
    setMemoryFormValues(defaultMemoryFormValues);
    setSubmitting(false);
  }, []);

  const handleKnowledgeFormChange = useCallback((field, value) => {
    setKnowledgeFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleMemoryFormChange = useCallback((field, value) => {
    setMemoryFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (dialogType === 'client' || dialogType === 'company') {
      const content = knowledgeFormValues.useStructuredDraft
        ? buildStructuredKnowledgeContent(knowledgeFormValues)
        : String(knowledgeFormValues.content || '');
      if (!knowledgeFormValues.title.trim()) {
        message.error('Title is required.');
        return;
      }
      if (!content.trim()) {
        message.error('Add document content or fill out the guided sections.');
        return;
      }

      const payload = {
        title: knowledgeFormValues.title.trim(),
        content,
        tags: parseTags(knowledgeFormValues.tags),
        document_type: knowledgeFormValues.document_type,
        status: knowledgeFormValues.status,
        source_of_truth: knowledgeFormValues.source_of_truth.trim(),
        priority: Number(knowledgeFormValues.priority || 3),
        recency: dayjs(knowledgeFormValues.recency).toISOString(),
      };

      setSubmitting(true);
      if (dialogType === 'client') {
        if (!knowledgeFormValues.client) {
          message.error('Select a client for this document.');
          setSubmitting(false);
          return;
        }
        payload.client = knowledgeFormValues.client;
        const request = editingItem
          ? knowledgeApi.updateClientDocument(editingItem.id, payload)
          : knowledgeApi.createClientDocument(payload);
        request
          .then(() => {
            message.success(editingItem ? 'Client knowledge updated.' : 'Client knowledge created.');
            closeDialog();
            loadClientDocs();
          })
          .catch((error) => {
            console.error('Failed to save client knowledge document', error);
            message.error('Could not save the client knowledge document.');
            setSubmitting(false);
          });
        return;
      }

      payload.audience = knowledgeFormValues.audience.trim();
      const request = editingItem
        ? knowledgeApi.updateCompanyDocument(editingItem.id, payload)
        : knowledgeApi.createCompanyDocument(payload);
      request
        .then(() => {
          message.success(editingItem ? 'Company knowledge updated.' : 'Company knowledge created.');
          closeDialog();
          loadCompanyDocs();
        })
        .catch((error) => {
          console.error('Failed to save company knowledge document', error);
          message.error('Could not save the company knowledge document.');
          setSubmitting(false);
        });
      return;
    }

    if (dialogType === 'memory' || dialogType === 'company-memory') {
      if (dialogType === 'memory' && !memoryFormValues.client) {
        message.error('Select a client for this memory item.');
        return;
      }
      if (!memoryFormValues.title.trim() || !memoryFormValues.summary.trim()) {
        message.error('Title and summary are required for memory items.');
        return;
      }

      const payload = {
        category: memoryFormValues.category,
        source_type: memoryFormValues.source_type,
        audience: memoryFormValues.audience.trim(),
        title: memoryFormValues.title.trim(),
        summary: memoryFormValues.summary.trim(),
        details: memoryFormValues.details.trim(),
        tags: parseTags(memoryFormValues.tags),
        confidence: Number(memoryFormValues.confidence || 1),
        priority: Number(memoryFormValues.priority || 3),
        is_active: Boolean(memoryFormValues.is_active),
        valid_from: dayjs(memoryFormValues.valid_from).toISOString(),
        valid_until: memoryFormValues.valid_until
          ? dayjs(memoryFormValues.valid_until).toISOString()
          : null,
        last_observed_at: dayjs(memoryFormValues.last_observed_at).toISOString(),
      };
      if (dialogType === 'memory') {
        payload.client = memoryFormValues.client;
      }

      setSubmitting(true);
      const request =
        dialogType === 'memory'
          ? editingItem
            ? knowledgeApi.updateClientMemoryItem(editingItem.id, payload)
            : knowledgeApi.createClientMemoryItem(payload)
          : editingItem
          ? knowledgeApi.updateCompanyMemoryItem(editingItem.id, payload)
          : knowledgeApi.createCompanyMemoryItem(payload);
      request
        .then(() => {
          message.success(
            dialogType === 'memory'
              ? editingItem
                ? 'Client memory updated.'
                : 'Client memory created.'
              : editingItem
              ? 'Company memory updated.'
              : 'Company memory created.'
          );
          closeDialog();
          if (dialogType === 'memory') {
            loadMemoryItems();
          } else {
            loadCompanyMemoryItems();
          }
        })
        .catch((error) => {
          console.error('Failed to save memory item', error);
          message.error(
            dialogType === 'memory'
              ? 'Could not save the client memory item.'
              : 'Could not save the company memory item.'
          );
          setSubmitting(false);
        });
    }
  }, [
    companyMemoryActiveFilter,
    loadCompanyMemoryItems,
    closeDialog,
    dialogType,
    editingItem,
    knowledgeFormValues,
    loadClientDocs,
    loadCompanyDocs,
    loadMemoryItems,
    memoryFormValues,
    message,
  ]);

  const confirmAndDelete = useCallback(
    (type, item) => {
      if (!item?.id) {
        return;
      }
      const confirmed = window.confirm(`Delete "${item.title}"? This action cannot be undone.`);
      if (!confirmed) {
        return;
      }

      const request =
        type === 'client'
          ? knowledgeApi.deleteClientDocument(item.id)
          : type === 'company'
          ? knowledgeApi.deleteCompanyDocument(item.id)
          : type === 'memory'
          ? knowledgeApi.deleteClientMemoryItem(item.id)
          : knowledgeApi.deleteCompanyMemoryItem(item.id);

      request
        .then(() => {
          message.success(
            type === 'memory'
              ? 'Client memory item deleted.'
              : type === 'company-memory'
              ? 'Company memory item deleted.'
              : 'Knowledge document deleted.'
          );
          if (type === 'client') {
            loadClientDocs();
          } else if (type === 'company') {
            loadCompanyDocs();
          } else if (type === 'memory') {
            loadMemoryItems();
          } else {
            loadCompanyMemoryItems();
          }
        })
        .catch((error) => {
          console.error('Failed to delete authoring item', error);
          message.error('Could not delete the selected item.');
        });
    },
    [loadClientDocs, loadCompanyDocs, loadCompanyMemoryItems, loadMemoryItems, message],
  );

  const structuredPreview = useMemo(
    () => buildStructuredKnowledgeContent(knowledgeFormValues),
    [knowledgeFormValues],
  );

  const dialogTitle =
    dialogType === 'client'
      ? editingItem
        ? 'Edit client knowledge'
        : 'Add client knowledge'
      : dialogType === 'company'
      ? editingItem
        ? 'Edit company knowledge'
        : 'Add company knowledge'
      : dialogType === 'company-memory'
      ? editingItem
        ? 'Edit company memory'
        : 'Add company memory'
      : editingItem
      ? 'Edit client memory'
      : 'Add client memory';

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4">Knowledge Base</Typography>
            <Typography variant="body2" color="text.secondary">
              Author durable knowledge documents and distilled client memory for the AI brain.
            </Typography>
          </Box>
        </Stack>

        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        {activeTab === 'client' ? (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Autocomplete
                  options={clientOptions}
                  loading={clientOptionsLoading}
                  onInputChange={handleClientAutocompleteInput}
                  value={findClientOptionByValue(clientFilter)}
                  onChange={(_, option) => setClientFilter(option ? option.value : null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter by client" placeholder="Search clients" />
                  )}
                  sx={{ minWidth: { xs: '100%', md: 260 } }}
                />
                <TextField
                  value={clientSearchInput}
                  onChange={(event) => setClientSearchInput(event.target.value)}
                  label="Search"
                  placeholder="Search title or content"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openKnowledgeDialog('client')}>
                  Add client knowledge
                </Button>
              </Stack>
            </CardContent>
            <Divider />
            {clientDocsLoading ? (
              <Box py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientDocs.length ? (
                      clientDocs.map((doc) => (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2">{doc.title}</Typography>
                              {renderPreview(doc.content)}
                            </Stack>
                          </TableCell>
                          <TableCell>{doc.client_name || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={doc.document_type || 'other'} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={doc.status || 'active'} />
                          </TableCell>
                          <TableCell>{doc.priority ?? 3}</TableCell>
                          <TableCell>{doc.updated_at ? dayjs(doc.updated_at).format('MMM D, YYYY') : '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton onClick={() => openKnowledgeDialog('client', doc)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => confirmAndDelete('client', doc)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No client knowledge documents found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : null}

        {activeTab === 'company' ? (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  value={companyAudience}
                  onChange={(event) => setCompanyAudience(event.target.value)}
                  label="Audience"
                  placeholder="sales, finance, account managers"
                  sx={{ minWidth: { xs: '100%', md: 260 } }}
                />
                <TextField
                  value={companySearchInput}
                  onChange={(event) => setCompanySearchInput(event.target.value)}
                  label="Search"
                  placeholder="Search title or content"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openKnowledgeDialog('company')}>
                  Add company knowledge
                </Button>
              </Stack>
            </CardContent>
            <Divider />
            {companyDocsLoading ? (
              <Box py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyDocs.length ? (
                      companyDocs.map((doc) => (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2">{doc.title}</Typography>
                              {renderPreview(doc.content)}
                            </Stack>
                          </TableCell>
                          <TableCell>{doc.audience || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={doc.document_type || 'other'} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={doc.status || 'active'} />
                          </TableCell>
                          <TableCell>{doc.priority ?? 3}</TableCell>
                          <TableCell>{doc.updated_at ? dayjs(doc.updated_at).format('MMM D, YYYY') : '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton onClick={() => openKnowledgeDialog('company', doc)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => confirmAndDelete('company', doc)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No company knowledge documents found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : null}

        {activeTab === 'memory' ? (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Autocomplete
                  options={clientOptions}
                  loading={clientOptionsLoading}
                  onInputChange={handleClientAutocompleteInput}
                  value={findClientOptionByValue(memoryClientFilter)}
                  onChange={(_, option) => setMemoryClientFilter(option ? option.value : null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter by client" placeholder="Search clients" />
                  )}
                  sx={{ minWidth: { xs: '100%', md: 240 } }}
                />
                <TextField
                  select
                  value={memoryCategoryFilter}
                  onChange={(event) => setMemoryCategoryFilter(event.target.value)}
                  label="Category"
                  sx={{ minWidth: { xs: '100%', md: 180 } }}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {MEMORY_CATEGORIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={memoryActiveFilter}
                  onChange={(event) => setMemoryActiveFilter(event.target.value)}
                  label="Status"
                  sx={{ minWidth: { xs: '100%', md: 160 } }}
                >
                  <MenuItem value="active">Active only</MenuItem>
                  <MenuItem value="inactive">Inactive only</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </TextField>
                <TextField
                  value={memorySearchInput}
                  onChange={(event) => setMemorySearchInput(event.target.value)}
                  label="Search"
                  placeholder="Search title or summary"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openMemoryDialog()}>
                  Add memory
                </Button>
              </Stack>
            </CardContent>
            <Divider />
            {memoryItemsLoading ? (
              <Box py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {memoryItems.length ? (
                      memoryItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2">{item.title}</Typography>
                              {renderPreview(item.summary)}
                            </Stack>
                          </TableCell>
                          <TableCell>{item.client_name || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={item.category || 'other'} />
                          </TableCell>
                          <TableCell>{item.confidence}</TableCell>
                          <TableCell>{item.priority ?? 3}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={item.is_active ? 'success' : 'default'}
                              label={item.is_active ? 'Active' : 'Inactive'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton onClick={() => openMemoryDialog(item)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => confirmAndDelete('memory', item)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No client memory items found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : null}

        {activeTab === 'company-memory' ? (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  value={companyMemoryAudienceFilter}
                  onChange={(event) => setCompanyMemoryAudienceFilter(event.target.value)}
                  label="Audience"
                  placeholder="sales, account managers, operations"
                  sx={{ minWidth: { xs: '100%', md: 240 } }}
                />
                <TextField
                  select
                  value={companyMemoryCategoryFilter}
                  onChange={(event) => setCompanyMemoryCategoryFilter(event.target.value)}
                  label="Category"
                  sx={{ minWidth: { xs: '100%', md: 180 } }}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {MEMORY_CATEGORIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={companyMemoryActiveFilter}
                  onChange={(event) => setCompanyMemoryActiveFilter(event.target.value)}
                  label="Status"
                  sx={{ minWidth: { xs: '100%', md: 160 } }}
                >
                  <MenuItem value="active">Active only</MenuItem>
                  <MenuItem value="inactive">Inactive only</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </TextField>
                <TextField
                  value={companyMemorySearchInput}
                  onChange={(event) => setCompanyMemorySearchInput(event.target.value)}
                  label="Search"
                  placeholder="Search title or summary"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openMemoryDialog('company-memory')}>
                  Add company memory
                </Button>
              </Stack>
            </CardContent>
            <Divider />
            {companyMemoryItemsLoading ? (
              <Box py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyMemoryItems.length ? (
                      companyMemoryItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2">{item.title}</Typography>
                              {renderPreview(item.summary)}
                            </Stack>
                          </TableCell>
                          <TableCell>{item.audience || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={item.category || 'other'} />
                          </TableCell>
                          <TableCell>{item.confidence}</TableCell>
                          <TableCell>{item.priority ?? 3}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={item.is_active ? 'success' : 'default'}
                              label={item.is_active ? 'Active' : 'Inactive'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton onClick={() => openMemoryDialog('company-memory', item)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => confirmAndDelete('company-memory', item)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No company memory items found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : null}

        <Dialog open={Boolean(dialogType)} onClose={closeDialog} fullWidth maxWidth="md">
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent dividers>
            {dialogType === 'client' || dialogType === 'company' ? (
              <Stack spacing={2}>
                {dialogType === 'client' ? (
                  <Autocomplete
                    options={clientOptions}
                    loading={clientOptionsLoading}
                    onInputChange={handleClientAutocompleteInput}
                    value={findClientOptionByValue(knowledgeFormValues.client)}
                    onChange={(_, option) => handleKnowledgeFormChange('client', option ? option.value : null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" placeholder="Search clients" />
                    )}
                  />
                ) : (
                  <TextField
                    label="Audience"
                    value={knowledgeFormValues.audience}
                    onChange={(event) => handleKnowledgeFormChange('audience', event.target.value)}
                    placeholder="sales, finance, account managers"
                  />
                )}

                <TextField
                  label="Title"
                  value={knowledgeFormValues.title}
                  onChange={(event) => handleKnowledgeFormChange('title', event.target.value)}
                  fullWidth
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Document type"
                    value={knowledgeFormValues.document_type}
                    onChange={(event) => handleKnowledgeFormChange('document_type', event.target.value)}
                    fullWidth
                  >
                    {KNOWLEDGE_DOCUMENT_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Status"
                    value={knowledgeFormValues.status}
                    onChange={(event) => handleKnowledgeFormChange('status', event.target.value)}
                    fullWidth
                  >
                    {KNOWLEDGE_STATUSES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Source of truth"
                    value={knowledgeFormValues.source_of_truth}
                    onChange={(event) => handleKnowledgeFormChange('source_of_truth', event.target.value)}
                    fullWidth
                    placeholder="Contract, SOP, approved playbook, etc."
                  />
                  <TextField
                    label="Priority"
                    type="number"
                    value={knowledgeFormValues.priority}
                    onChange={(event) => handleKnowledgeFormChange('priority', event.target.value)}
                    inputProps={{ min: 1, max: 5 }}
                    sx={{ width: { xs: '100%', md: 160 } }}
                  />
                </Stack>

                <TextField
                  label="Tags"
                  value={knowledgeFormValues.tags}
                  onChange={(event) => handleKnowledgeFormChange('tags', event.target.value)}
                  placeholder="billing, pacing, approvals"
                  helperText="Comma-separated"
                />

                <DateTimePicker
                  label="Recency"
                  value={knowledgeFormValues.recency}
                  onChange={(value) => handleKnowledgeFormChange('recency', value || dayjs())}
                  slotProps={{ textField: { fullWidth: true } }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={knowledgeFormValues.useStructuredDraft}
                      onChange={(event) =>
                        handleKnowledgeFormChange('useStructuredDraft', event.target.checked)
                      }
                    />
                  }
                  label="Use guided knowledge sections"
                />

                {knowledgeFormValues.useStructuredDraft ? (
                  <Stack spacing={2}>
                    <TextField
                      label="Purpose"
                      value={knowledgeFormValues.purpose}
                      onChange={(event) => handleKnowledgeFormChange('purpose', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Policy / Truth"
                      value={knowledgeFormValues.policy}
                      onChange={(event) => handleKnowledgeFormChange('policy', event.target.value)}
                      multiline
                      minRows={3}
                    />
                    <TextField
                      label="How It Works"
                      value={knowledgeFormValues.how_it_works}
                      onChange={(event) => handleKnowledgeFormChange('how_it_works', event.target.value)}
                      multiline
                      minRows={3}
                    />
                    <TextField
                      label="Positioning Notes"
                      value={knowledgeFormValues.positioning}
                      onChange={(event) => handleKnowledgeFormChange('positioning', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Client-Facing Template"
                      value={knowledgeFormValues.response_template}
                      onChange={(event) => handleKnowledgeFormChange('response_template', event.target.value)}
                      multiline
                      minRows={3}
                    />
                    <TextField
                      label="FAQ"
                      value={knowledgeFormValues.faq}
                      onChange={(event) => handleKnowledgeFormChange('faq', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Restrictions / Do Not Say"
                      value={knowledgeFormValues.restrictions}
                      onChange={(event) => handleKnowledgeFormChange('restrictions', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Escalation"
                      value={knowledgeFormValues.escalation}
                      onChange={(event) => handleKnowledgeFormChange('escalation', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Additional Notes"
                      value={knowledgeFormValues.notes}
                      onChange={(event) => handleKnowledgeFormChange('notes', event.target.value)}
                      multiline
                      minRows={2}
                    />
                    <Box p={2} borderRadius={1} bgcolor="grey.50">
                      <Typography variant="subtitle2">Generated content preview</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                        {structuredPreview || 'Fill out the sections to build the document content.'}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <TextField
                    label="Raw content"
                    value={knowledgeFormValues.content}
                    onChange={(event) => handleKnowledgeFormChange('content', event.target.value)}
                    multiline
                    minRows={14}
                    fullWidth
                  />
                )}
              </Stack>
            ) : null}

            {dialogType === 'memory' || dialogType === 'company-memory' ? (
              <Stack spacing={2}>
                {dialogType === 'memory' ? (
                  <Autocomplete
                    options={clientOptions}
                    loading={clientOptionsLoading}
                    onInputChange={handleClientAutocompleteInput}
                    value={findClientOptionByValue(memoryFormValues.client)}
                    onChange={(_, option) => handleMemoryFormChange('client', option ? option.value : null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" placeholder="Search clients" />
                    )}
                  />
                ) : (
                  <TextField
                    label="Audience"
                    value={memoryFormValues.audience}
                    onChange={(event) => handleMemoryFormChange('audience', event.target.value)}
                    placeholder="sales, account managers, operations"
                    fullWidth
                  />
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Category"
                    value={memoryFormValues.category}
                    onChange={(event) => handleMemoryFormChange('category', event.target.value)}
                    fullWidth
                  >
                    {MEMORY_CATEGORIES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Source type"
                    value={memoryFormValues.source_type}
                    onChange={(event) => handleMemoryFormChange('source_type', event.target.value)}
                    fullWidth
                  >
                    {MEMORY_SOURCE_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <TextField
                  label="Title"
                  value={memoryFormValues.title}
                  onChange={(event) => handleMemoryFormChange('title', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Summary"
                  value={memoryFormValues.summary}
                  onChange={(event) => handleMemoryFormChange('summary', event.target.value)}
                  multiline
                  minRows={3}
                  helperText="Short distilled fact the AI should keep in mind."
                />
                <TextField
                  label="Details"
                  value={memoryFormValues.details}
                  onChange={(event) => handleMemoryFormChange('details', event.target.value)}
                  multiline
                  minRows={4}
                  helperText="Optional supporting detail or nuance."
                />
                <TextField
                  label="Tags"
                  value={memoryFormValues.tags}
                  onChange={(event) => handleMemoryFormChange('tags', event.target.value)}
                  placeholder="approvals, tone, booked appointments"
                  helperText="Comma-separated"
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Confidence"
                    type="number"
                    value={memoryFormValues.confidence}
                    onChange={(event) => handleMemoryFormChange('confidence', event.target.value)}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    fullWidth
                  />
                  <TextField
                    label="Priority"
                    type="number"
                    value={memoryFormValues.priority}
                    onChange={(event) => handleMemoryFormChange('priority', event.target.value)}
                    inputProps={{ min: 1, max: 5 }}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <DateTimePicker
                    label="Valid from"
                    value={memoryFormValues.valid_from}
                    onChange={(value) => handleMemoryFormChange('valid_from', value || dayjs())}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DateTimePicker
                    label="Valid until"
                    value={memoryFormValues.valid_until}
                    onChange={(value) => handleMemoryFormChange('valid_until', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>

                <DateTimePicker
                  label="Last observed at"
                  value={memoryFormValues.last_observed_at}
                  onChange={(value) => handleMemoryFormChange('last_observed_at', value || dayjs())}
                  slotProps={{ textField: { fullWidth: true } }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={memoryFormValues.is_active}
                      onChange={(event) => handleMemoryFormChange('is_active', event.target.checked)}
                    />
                  }
                  label="Active memory item"
                />
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
              {submitting ? 'Saving…' : editingItem ? 'Save changes' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

export default KnowledgeBasePage;
